// Perspective detection for the rewrite engine — PURELY ADDITIVE.
//
// Decides whether the original message is:
//   - "other_directed": dominant subject is You/Your/They/He/She/It/Him/Her/Them
//   - "self_directed" : dominant subject is I/We/My/Our/Me
//
// The result is passed to the backend along with /api/tone/analyze and locks
// the rewrite perspective — see server.py _perspective_lock_block().
//
// Safe default = "self_directed" (per spec). This is the conservative choice
// because softening a self-directed message can never accidentally absolve
// the user of accountability, whereas the inverse (treating an
// other-directed rant as self-directed) is what produces the broken
// "I apologize for handling this poorly" output we are fixing.
//
// Algorithm: tokenize → lowercase → weight subject pronouns (I/you/we/they/
// he/she/it) at 2x, possessive/object pronouns at 1x → compare totals.
// Mirrors backend `_detect_perspective` exactly so client and server agree.

const OTHER_DIRECTED_TOKENS = new Set([
    // 2nd person
    "you",
    "your",
    "yours",
    "you're",
    "you've",
    "you'd",
    "you'll",
    // 3rd person plural
    "they",
    "their",
    "theirs",
    "them",
    "they're",
    "they've",
    "they'd",
    "they'll",
    // 3rd person singular masculine
    "he",
    "his",
    "him",
    "he's",
    "he'd",
    "he'll",
    // 3rd person singular feminine
    "she",
    "her",
    "hers",
    "she's",
    "she'd",
    "she'll",
    // 3rd person singular neuter (explicitly listed in spec)
    "it",
    "its",
    "it's",
]);

const SELF_DIRECTED_TOKENS = new Set([
    // 1st person singular
    "i",
    "me",
    "my",
    "mine",
    "i'm",
    "i've",
    "i'd",
    "i'll",
    // 1st person plural
    "we",
    "us",
    "our",
    "ours",
    "we're",
    "we've",
    "we'd",
    "we'll",
]);

// Subject-position pronouns carry stronger signal about *who the message is
// directed at* than possessives / objects. "it" is intentionally NOT in this
// set — "it" often refers to objects/tasks ("I'll handle it") rather than a
// person and would otherwise out-weight 1st-person possessives.
const SUBJECT_HEAVY = new Set(["i", "you", "we", "they", "he", "she"]);

export const PERSPECTIVE_OTHER = "other_directed";
export const PERSPECTIVE_SELF = "self_directed";

/**
 * Detect the dominant perspective of a message.
 * @param {string} text
 * @returns {"other_directed" | "self_directed"}
 */
export function detectPerspective(text) {
    if (typeof text !== "string" || !text.trim()) {
        return PERSPECTIVE_SELF; // safe default
    }

    // Tokenize: keep letters + apostrophes (so contractions stay intact),
    // split on everything else.
    const tokens = text.toLowerCase().match(/[a-z']+/g) || [];

    let otherWeight = 0;
    let selfWeight = 0;

    for (const tok of tokens) {
        const w = SUBJECT_HEAVY.has(tok) ? 2 : 1;
        if (OTHER_DIRECTED_TOKENS.has(tok)) {
            otherWeight += w;
        } else if (SELF_DIRECTED_TOKENS.has(tok)) {
            selfWeight += w;
        }
    }

    if (otherWeight > selfWeight) return PERSPECTIVE_OTHER;
    // tie or both zero → safe default per spec
    return PERSPECTIVE_SELF;
}

const _exports = {
    detectPerspective,
    PERSPECTIVE_OTHER,
    PERSPECTIVE_SELF,
};

export default _exports;
